import { useState, useTransition, useCallback } from 'react';
import { NEXT_REST_ENTITY, RestEntityQueryResponseMap } from '@common/interfaces';
import { getCachedToken } from '@config/authentication';
import { nextRestApiCallWithToken } from '@utils/serverActions/clientUtility';

export interface IUseRestLazyCallOptions<MAPPED_ENTITY extends NEXT_REST_ENTITY> {
  body?: Record<string, any>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  nextOptions?: Record<string, any>;
  skip?: boolean;
  orderId?: string;
  onCompleted?: (data: RestEntityQueryResponseMap[MAPPED_ENTITY]) => void;
  onError?: (error: any) => void;
}

interface IUseRestLazyCallResult<MAPPED_ENTITY extends NEXT_REST_ENTITY> {
  loading: boolean;
  data: RestEntityQueryResponseMap[MAPPED_ENTITY] | null;
  error: any;
}

type TUseRestLazyCallReturn<MAPPED_ENTITY extends NEXT_REST_ENTITY> = [
  (
    overrides?: Partial<Pick<IUseRestLazyCallOptions<MAPPED_ENTITY>, 'body' | 'method' | 'orderId'>>
  ) => Promise<RestEntityQueryResponseMap[MAPPED_ENTITY]>,
  IUseRestLazyCallResult<MAPPED_ENTITY>,
];

export function useRestLazyCall<MAPPED_ENTITY extends NEXT_REST_ENTITY>(
  entity: MAPPED_ENTITY,
  options: IUseRestLazyCallOptions<MAPPED_ENTITY> = {}
): TUseRestLazyCallReturn<MAPPED_ENTITY> {
  const {
    body = {},
    method = 'GET',
    nextOptions = { cache: 'no-store' },
    skip = false,
    orderId,
    onCompleted,
    onError,
  } = options;

  const [isPending, startTransition] = useTransition();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RestEntityQueryResponseMap[MAPPED_ENTITY] | null>(null);
  const [error, setError] = useState<any>(null);

  const execute = useCallback(
    async (overrides?: Partial<Pick<IUseRestLazyCallOptions<MAPPED_ENTITY>, 'body' | 'method' | 'orderId'>>) => {
      if (skip) return null;
      setLoading(true);
      try {
        const resolvedBody = overrides?.body ?? body;
        const resolvedMethod = overrides?.method ?? method;
        const resolvedOrderId = overrides?.orderId ?? orderId;

        const result = await nextRestApiCallWithToken(
          entity,
          getCachedToken(),
          resolvedMethod,
          resolvedBody,
          nextOptions,
          resolvedOrderId
        );

        startTransition(() => {
          setData(result);
          setError(null);
        });

        onCompleted?.(result);
        return result;
      } catch (err) {
        startTransition(() => {
          setError(err);
        });

        onError?.(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [entity, JSON.stringify(body), skip, method, orderId]
  );

  return [
    execute,
    {
      loading,
      data,
      error,
    },
  ];
}
